import { Component, Host, h, State, Prop, Method } from "@stencil/core";
import { renderItem as mercuryChatRenderItem } from "@genexus/mercury";
import { sendContainerLayout as mercurySendContainerLayout } from "@genexus/mercury";
import { codeFixerRecord } from "./models";

const CSS_BUNDLES = [
  "resets/box-sizing",
  "utils/elevation",
  "components/chat",
  "components/markdown-viewer"
];

import { chatCallbacksWithChatRef, chatTranslations } from "./callbacks";
import {
  ChatMessage,
  ChatCallbacks
} from "@genexus/chameleon-controls-library";
import { ChatMessageUser } from "@genexus/chameleon-controls-library/dist/types/components/chat/types";

@Component({
  tag: "gx-ide-sc-chat-container",
  styleUrl: "chat-showcase.scss",
  shadow: true
})
export class GxIdeScChatContainer {
  #chatContainerRef!: HTMLGxIdeChatContainerElement;
  #chatWelcomeRef!: HTMLGxIdeChatWelcomeElement;
  #chChatRef!: HTMLChChatElement;
  #chatCallbacks: ChatCallbacks;

  /**
   * If true, the chat items length will be 0, since this is a requirement for dipslaying the ch-chat
   * "empty-chat" slot that welcome screen uses to display.
   */
  @Prop() readonly mode: "welcome" | "messages-sample" | "empty" =
    "messages-sample";

  @State() generatingResponse: boolean = false;
  @State() loadingState:
    | "all-records-loaded"
    | "initial"
    | "loading"
    | "more-data-to-fetch" = "all-records-loaded";
  @State() showAdditionalContent: boolean = false;
  @State() conversationCopied: boolean = false;
  @State() assistantStatus: string = "Getting Data";
  @State() chatItems: ChatMessage[] = [];

  /**
   * It will exit the wellcome screen and remove it from the dom.
   */
  @Method()
  async exitWelcome() {
    // hand exitWelcome to gx-ide-chat-welcome
    this.#chatWelcomeRef.exitWelcome();
  }

  #copyConversationCallbackHandler = () => {
    this.#chatContainerRef.showCopyConversationMessage();
  };

  #deleteConversationCallbackHandler = () => {};

  componentDidLoad() {
    setTimeout(() => {
      this.assistantStatus = "Creating Data Base..";
    }, 1000);
  }

  componentDidRender() {
    if (!this.#chatCallbacks && this.#chChatRef) {
      this.#chatCallbacks = chatCallbacksWithChatRef(this.#chChatRef);
    }
  }

  async componentWillLoad() {
    if (this.mode === "messages-sample") {
      this.chatItems = codeFixerRecord;
    } else if (this.mode === "welcome") {
      this.chatItems = [];
    } else if (this.mode === "empty") {
      this.chatItems = [
        {
          id: "1",
          role: "assistant",
          status: "complete",
          content: "Hello! I am your assistant.",
          metadata: "14:56"
        }
      ];
    }
  }
  #suggestedPrompRequestedCallbackHandler = async (
    prompt: string
  ): Promise<void> => {
    let assistantFinalResponse: string;
    if (prompt.toLowerCase().includes("transaction")) {
      assistantFinalResponse =
        'To create a transaction in GeneXus, start by opening your KB (Knowledge Base) and selecting "Transaction" from the "New Object" options. Give it a name and define its attributes, starting with a primary key followed by the related data fields. GeneXus will automatically infer the data structure and relationships based on how you define the attributes and their nesting. Once saved, it generates the necessary database tables and forms for inserting, updating, and deleting records. You can then run the application to test the transaction directly in your browser.';
    } else if (prompt.toLowerCase().includes("entity")) {
      assistantFinalResponse =
        'In GeneXus, creating an entity usually means defining a Transaction object that represents a real-world concept like "Customer" or "Product." To do this, create a new Transaction, name it after the entity, and define its attributes—starting with a unique identifier (like CustomerId) and then adding other fields (like CustomerName, CustomerEmail). GeneXus treats this Transaction as an entity and uses it to generate the corresponding table and interface automatically.';
    } else {
      assistantFinalResponse =
        "Sorry, I didn’t catch that—mind rephrasing it? 😅";
    }

    const userPrompt: ChatMessageUser = {
      id: "1",
      role: "user",
      content: prompt
    };
    this.#chChatRef.addNewMessage(userPrompt);
    setTimeout(() => {
      this.#chChatRef.addNewMessage({
        id: `${new Date().getTime()}`,
        role: "assistant",
        content: "Analyzing",
        status: "waiting"
      });
      setTimeout(() => {
        this.#chChatRef.updateLastMessage(
          {
            role: "assistant",
            content: "Processing with Chat with LLMs",
            status: "waiting"
          },
          "replace"
        );
        setTimeout(() => {
          this.#chChatRef.updateLastMessage(
            {
              role: "assistant",
              content: "Getting information",
              status: "waiting"
            },
            "replace"
          );
          this.#chChatRef.updateLastMessage(
            {
              role: "assistant",
              content: assistantFinalResponse,
              status: "complete"
            },
            "replace"
          );
        }, 1500);
      }, 1500);
    }, 100);
  };

  render() {
    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <gx-ide-chat-container
          chatTitle="GeneXus Assistant"
          copyConversationCallback={this.#copyConversationCallbackHandler}
          deleteConversationCallback={this.#deleteConversationCallbackHandler}
          ref={el =>
            (this.#chatContainerRef = el as HTMLGxIdeChatContainerElement)
          }
          class="elevation-1"
        >
          <ch-chat
            callbacks={this.#chatCallbacks}
            class="chat"
            waitingResponse={this.generatingResponse}
            loadingState={this.loadingState} // "all-records-loaded" to display the welcome screen
            renderItem={mercuryChatRenderItem}
            items={this.chatItems} // length 0 to display the welcome screen
            showAdditionalContent={false}
            sendContainerLayout={mercurySendContainerLayout}
            translations={chatTranslations}
            ref={(el: HTMLChChatElement) =>
              (this.#chChatRef = el as HTMLChChatElement)
            }
            markdownTheme="components/markdown-viewer"
          >
            <gx-ide-chat-welcome
              ref={el =>
                (this.#chatWelcomeRef = el as HTMLGxIdeChatWelcomeElement)
              }
              slot="empty-chat"
              suggestedPrompRequestedCallback={
                this.#suggestedPrompRequestedCallbackHandler
              }
            ></gx-ide-chat-welcome>
            <div slot="additional-content">
              Custom content that is rendered when the chat renders content
            </div>
          </ch-chat>
        </gx-ide-chat-container>
      </Host>
    );
  }
}
